import { Material } from '@/gql/graphql';
import {
  Button,
  Conditional,
  EclipseBadge,
  EmptyState,
} from '@payaca/ambrosia';
import List from '@payaca/components/list/List';

import { Nullish } from '@payaca/utilities/types';
import { MaterialCardWithTransition } from '../materialCard/MaterialCard';

export type LinkedMaterial = {
  id: Material['id'];
  name: Material['name'];
  suppliedBy: {
    price: {
      unitPriceExcTax: Material['suppliedBy'][number]['price']['unitPriceExcTax'];
    };
    supplier: {
      name: Material['suppliedBy'][number]['supplier']['name'];
    };
  }[];
  tags: {
    name: Material['tags'][number]['name'];
    colour?: Nullish<Material['tags'][number]['colour']>;
  }[];
  image?: Nullish<{
    thumbnailUrl?: Nullish<string>;
  }>;
  quantity: number;
};

export interface IProps<T extends LinkedMaterial> {
  linkedMaterials: T[];
  titleText?: string;
  onLinkMaterialsRequest?: () => void;
  onChange?: (newLinkedMaterials: T[]) => void;
  /**
   * When true, hides the "Link materials to item" button and disables
   * each material card's quantity input.
   */
  readOnly?: boolean;
}

const LinkedMaterialsList = <T extends LinkedMaterial>(props: IProps<T>) => {
  const {
    linkedMaterials,
    onLinkMaterialsRequest,
    titleText = 'Linked materials',
    onChange,
    readOnly,
  } = props;

  const showLinkButton = !readOnly && !!onLinkMaterialsRequest;

  const numOfLinkedMaterials = linkedMaterials.reduce((acc, linkedMaterial) => {
    return acc + linkedMaterial.quantity;
  }, 0);

  return (
    <>
      <List
        items={linkedMaterials}
        prefix={
          <>
            <h4>
              {titleText} ({numOfLinkedMaterials})
            </h4>

            <Conditional condition={numOfLinkedMaterials > 0 && showLinkButton}>
              <Button
                className="w-full"
                variant="secondary"
                size={'sm'}
                onClick={onLinkMaterialsRequest}
              >
                Link materials to item
              </Button>
            </Conditional>
          </>
        }
        item={(material) => {
          return (
            <MaterialCardWithTransition
              key={material.id}
              show={material.quantity > 0}
              afterLeave={() => {
                onChange?.(linkedMaterials.filter((i) => i.quantity > 0));
              }}
              name={material.name}
              price={material.suppliedBy[0]?.price.unitPriceExcTax}
              thumbnailUrl={material.image?.thumbnailUrl || ''}
              quantity={material.quantity}
              quantityDisabled={readOnly}
              tags={material.tags}
              onChangeQuantity={(newQuantity) => {
                onChange?.(
                  linkedMaterials.map((i) =>
                    i.id === material.id ? { ...i, quantity: newQuantity } : i
                  )
                );
              }}
            >
              <EclipseBadge
                badges={material.suppliedBy.map((i) => i.supplier.name)}
                variant="soft"
                colour="gray"
              />
            </MaterialCardWithTransition>
          );
        }}
      />
      <Conditional condition={numOfLinkedMaterials === 0}>
        <div className="my-auto text-center">
          <EmptyState iconName="link-broken-01.3" text="No linked materials" />

          <Conditional condition={showLinkButton}>
            <Button
              className="mt-3.5"
              variant="secondary"
              size={'sm'}
              onClick={onLinkMaterialsRequest}
            >
              Link materials to item
            </Button>
          </Conditional>
        </div>
      </Conditional>
    </>
  );
};

export default LinkedMaterialsList;
